import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { AdminAPI } from '../../lib/api';
import { downloadCsv } from '../../lib/csv';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const AdminLeads = () => {
  const { toast } = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try { setLeads(await AdminAPI.listBlogLeads()); }
    catch { toast({ title: 'Error', description: 'Could not load leads.' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const articles = useMemo(() => {
    const map = {};
    leads.forEach((l) => { map[l.slug] = l.article_title || l.slug; });
    return Object.entries(map);
  }, [leads]);

  const filtered = filter === 'all' ? leads : leads.filter((l) => l.slug === filter);

  const exportCsv = () => {
    downloadCsv('proofnation-blog-leads.csv', [
      { label: 'Date', value: (r) => fmt(r.created_at) },
      { label: 'Name', value: 'name' },
      { label: 'Email', value: 'email' },
      { label: 'Company', value: 'company' },
      { label: 'Article', value: (r) => r.article_title || r.slug },
      { label: 'Slug', value: 'slug' },
    ], filtered);
    toast({ title: 'Exported', description: `${filtered.length} leads downloaded as CSV.` });
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black text-[#050F2E] tracking-tight">Blog leads</h1>
          <p className="text-slate-500 mt-1">{leads.length} downloads · people who requested a checklist or scorecard</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-11 rounded-xl border border-slate-200 px-3 text-sm font-semibold" data-testid="leads-filter">
            <option value="all">All articles</option>
            {articles.map(([slug, title]) => <option key={slug} value={slug}>{title}</option>)}
          </select>
          <Button onClick={exportCsv} disabled={!filtered.length} className="bg-[#FF4E50] hover:bg-[#FF7273] text-[#050F2E] rounded-full h-11 px-6 font-bold" data-testid="leads-export">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center text-slate-400">
          <FileText className="h-8 w-8 mx-auto mb-3 opacity-50" />
          No leads yet. They appear here when a visitor downloads a checklist.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Company</th>
                <th className="text-left p-4">Article</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                  <td className="p-4 text-slate-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                  <td className="p-4 font-bold text-[#050F2E]">{l.name}</td>
                  <td className="p-4"><a href={`mailto:${l.email}`} className="text-[#0A2A63] hover:underline">{l.email}</a></td>
                  <td className="p-4 text-slate-600">{l.company || '—'}</td>
                  <td className="p-4 text-slate-600">{l.article_title || l.slug}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
